import { ref, onMounted, onUnmounted, watch, reactive, nextTick } from 'vue';

const activeMenuId = ref(null);
const position = ref({ x: 0, y: 0 });
const copiedIndex = ref(null);
let menuIdCounter = 0;

export function useContextMenu() {
    const menuId = menuIdCounter++;

    const isOpen = ref(false);
    const openSubmenus = reactive(new Set());
    const submenuTimeouts = new Map();
    const itemRefs = new Map();
    const submenuRefs = new Map();
    let closeTimeout = null;

    const OPEN_DELAY = 150;
    const CLOSE_DELAY = 150;

    const openMenu = (event) => {
        event.preventDefault();

        activeMenuId.value = menuId;

        position.value = { x: event.clientX, y: event.clientY };
        isOpen.value = true;
    };

    const closeMenu = (delay = 0) => {
        if (closeTimeout) clearTimeout(closeTimeout);
        if (delay > 0) {
            closeTimeout = setTimeout(() => {
                isOpen.value = false;
                if (activeMenuId.value === menuId) {
                    activeMenuId.value = null;
                }
                closeTimeout = null;
            }, delay);
        } else {
            isOpen.value = false;
            if (activeMenuId.value === menuId) {
                activeMenuId.value = null;
            }
        }
    };

    watch(activeMenuId, (newId) => {
        const isActiveMenu = newId === menuId;
        if (!isActiveMenu && isOpen.value) {
            isOpen.value = false;
        }
    });

    watch(isOpen, (newVal) => {
        if (!newVal) {
            openSubmenus.clear();
            submenuTimeouts.forEach(timeout => clearTimeout(timeout));
            submenuTimeouts.clear();
        }
    });

    const clickOutside = (event) => {
        if (!event.target.closest('.context-menu')) closeMenu();
    };

    onMounted(() => {
        document.addEventListener('click', clickOutside);
    });

    onUnmounted(() => {
        document.removeEventListener('click', clickOutside);

        if (closeTimeout) {
            clearTimeout(closeTimeout);
            closeTimeout = null;
        }

        submenuTimeouts.forEach(timeout => clearTimeout(timeout));
        submenuTimeouts.clear();

        itemRefs.clear();
        submenuRefs.clear();
    });

    const itemClicked = (item, index, event) => {
        if (item.disabled) return;

        if (item.url) {
            if (event.metaKey || event.ctrlKey) {
                window.open(item.url, '_blank');
                return;
            } else {
                window.location.href = item.url;
            }
        } else if (item.copy && typeof item.copy === 'string') {
            try {
                navigator.clipboard.writeText(item.copy)
                    .then(() => {
                        console.log('Copied to clipboard:', item.copy);
                        copiedIndex.value = index
                        setTimeout(() => {
                            copiedIndex.value = null
                            closeMenu(300)
                        }, 300)
                    })
                    .catch(err => {
                        console.error('Clipboard write failed:', err);
                    });
            } catch (err) {
                console.error('Clipboard write error:', err);
            }

            return;
        }

        closeMenu(250)
    };

    const clearSubmenuTimeout = (index) => {
        const timeout = submenuTimeouts.get(index);
        if (timeout) {
            clearTimeout(timeout);
            submenuTimeouts.delete(index);
        }
    };

    const handleParentEnter = (index, positionCallback) => {
        clearSubmenuTimeout(index);

        if (openSubmenus.has(index)) {
            return;
        }

        const timeout = setTimeout(() => {
            openSubmenus.add(index);
            submenuTimeouts.delete(index);
            if (positionCallback) {
                nextTick(() => {
                    positionCallback(index);
                });
            }
        }, OPEN_DELAY);

        submenuTimeouts.set(index, timeout);
    };

    const handleParentLeave = (index) => {
        clearSubmenuTimeout(index);

        if (!openSubmenus.has(index)) {
            return;
        }

        const timeout = setTimeout(() => {
            openSubmenus.delete(index);
            submenuTimeouts.delete(index);
        }, CLOSE_DELAY);

        submenuTimeouts.set(index, timeout);
    };

    const handleSubmenuEnter = (index) => {
        clearSubmenuTimeout(index);
        openSubmenus.add(index);
    };

    const handleSubmenuLeave = (index) => {
        clearSubmenuTimeout(index);

        const timeout = setTimeout(() => {
            openSubmenus.delete(index);
            submenuTimeouts.delete(index);
        }, CLOSE_DELAY);

        submenuTimeouts.set(index, timeout);
    };

    const setItemRef = (el, index) => {
        if (el) {
            itemRefs.set(index, el);
        }
    };

    const setSubmenuRef = (el, index) => {
        if (el) {
            submenuRefs.set(index, el);
        }
    };

    const getItemRef = (index) => itemRefs.get(index);
    const getSubmenuRef = (index) => submenuRefs.get(index);

    const createItem = (label, action, options = {}) => ({ type: 'item', label, action, ...options });
    const createSeparator = () => ({ type: 'separator' });
    const createSubmenu = (label, children, options = {}) => ({ type: 'submenu', label, children, ...options });

    return {
        isOpen,
        position,
        copiedIndex,
        openMenu,
        closeMenu,
        itemClicked,
        createItem,
        createSeparator,
        createSubmenu,
        openSubmenus,
        handleParentEnter,
        handleParentLeave,
        handleSubmenuEnter,
        handleSubmenuLeave,
        setItemRef,
        setSubmenuRef,
        getItemRef,
        getSubmenuRef
    };
}